import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useCallback, useEffect, useState } from "react";
import { useGetAvailableClients } from "../api/useGetAvailableClients";
import { useGetClientRequirements } from "../api/useGetClientRequirements";
import { useGetLlmClientDetails } from "../api/useGetLlmClientDetails";
import { useCreateLlmClient } from "../api/useCreateLlmClient";
import { useUpdateLlmClient } from "../api/useUpdateLlmClient";
import { LlmClientCapabilities } from "../models/Capabilities";
import { LlmClientTokenPricing } from "../models/TokenPricing";
import { LlmClientRateLimits } from "../models/RateLimits";
import { LlmClient } from "../models/LlmClient";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import CenterLoader from "@/shared/components/loader/CenterLoader";

interface AddEditClientModalProps {
  open: boolean;
  onClose: () => void;
  llmName: string;
  id?: string; // If provided, it's edit mode
  onSuccess?: () => void; // Called when client is successfully created/updated
}

export function AddEditClientModal({
  open,
  onClose,
  llmName,
  id,
  onSuccess,
}: AddEditClientModalProps) {
  const {
    availableClients,
    getAvailableClients,
    isLoading: isLoadingClients,
  } = useGetAvailableClients();
  const {
    clientRequirements,
    getClientRequirements,
    isLoading: isLoadingRequirements,
  } = useGetClientRequirements();
  const {
    llmClientDetails,
    getLlmClientDetails,
    isLoading: isLoadingDetails,
  } = useGetLlmClientDetails();
  const {
    createLlmClient,
    isCreated,
    isFailed: isCreateFailed,
  } = useCreateLlmClient();
  const {
    updateLlmClient,
    isUpdated,
    isFailed: isUpdateFailed,
  } = useUpdateLlmClient();

  const [selectedClientName, setSelectedClientName] = useState<string>("");
  const [requirementValues, setRequirementValues] = useState<
    Record<string, string>
  >({});
  const [capabilities, setCapabilities] = useState<LlmClientCapabilities>(
    new LlmClientCapabilities(),
  );
  const [tokenPricing, setTokenPricing] = useState<LlmClientTokenPricing>(
    new LlmClientTokenPricing(),
  );
  const [rateLimits, setRateLimits] = useState<LlmClientRateLimits>(
    new LlmClientRateLimits(),
  );
  const [rateLimitEnabled, setRateLimitEnabled] = useState({
    requestsPerMinute: false,
    tokensPerMinute: false,
    tokenPerDay: false,
  });
  const [note, setNote] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  const isEditMode = !!id;

  // Load available clients when modal opens
  useEffect(() => {
    if (open) {
      getAvailableClients();
      if (isEditMode && id) {
        getLlmClientDetails(llmName, id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditMode, id, llmName]);

  // Load requirements when client name changes
  useEffect(() => {
    if (selectedClientName) {
      getClientRequirements(selectedClientName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientName]);

  // Set existing values when editing
  useEffect(() => {
    if (llmClientDetails && isEditMode) {
      setSelectedClientName(llmClientDetails.clientName);
      setRequirementValues(llmClientDetails.requirements);
      setCapabilities(new LlmClientCapabilities(llmClientDetails.capabilities));
      setTokenPricing(new LlmClientTokenPricing(llmClientDetails.tokenPricing));
      setRateLimits(new LlmClientRateLimits(llmClientDetails.rateLimits));
      setNote(llmClientDetails.note || "");

      // Set rate limit enabled flags based on existing values
      setRateLimitEnabled({
        requestsPerMinute:
          llmClientDetails.rateLimits.requestsPerMinute !== null,
        tokensPerMinute: llmClientDetails.rateLimits.tokensPerMinute !== null,
        tokenPerDay: llmClientDetails.rateLimits.tokenPerDay !== null,
      });
    }
  }, [llmClientDetails, isEditMode]);

  const handleClose = useCallback(() => {
    setSelectedClientName("");
    setRequirementValues({});
    setNote("");
    onClose();
  }, [onClose]);

  // Reset requirement values when client changes (add mode only)
  useEffect(() => {
    if (!isEditMode && clientRequirements) {
      const initialValues: Record<string, string> = {};
      clientRequirements.forEach((req) => {
        initialValues[req] = "";
      });
      setRequirementValues(initialValues);
    }
  }, [clientRequirements, isEditMode]);

  // Close modal on successful create
  useEffect(() => {
    if (isCreated && !isCreateFailed) {
      onSuccess?.(); // Trigger refresh in parent
      handleClose();
    }
  }, [isCreated, isCreateFailed, onSuccess, handleClose]);

  // Close modal on successful update
  useEffect(() => {
    if (isUpdated && !isUpdateFailed) {
      onSuccess?.(); // Trigger refresh in parent
      handleClose();
    }
  }, [isUpdated, isUpdateFailed, onSuccess, handleClose]);

  const handleClientNameChange = (newClientName: string) => {
    if (!isEditMode) {
      setSelectedClientName(newClientName);
      setRequirementValues({}); // Reset values when changing client
    }
  };

  const handleRequirementChange = (requirementName: string, value: string) => {
    setRequirementValues((prev) => ({
      ...prev,
      [requirementName]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!selectedClientName) return;

    // Prepare rate limits with null for disabled fields
    const finalRateLimits: LlmClientRateLimits = {
      requestsPerMinute: rateLimitEnabled.requestsPerMinute
        ? rateLimits.requestsPerMinute
        : null,
      tokensPerMinute: rateLimitEnabled.tokensPerMinute
        ? rateLimits.tokensPerMinute
        : null,
      tokenPerDay: rateLimitEnabled.tokenPerDay ? rateLimits.tokenPerDay : null,
    };

    // Create client data object
    const baseClientData = {
      llmName: llmName,
      clientName: selectedClientName,
      isEnabled: true, // Default to enabled for new clients
      note: note.trim() || undefined,
      requirements: {
        ...requirementValues,
        name: selectedClientName, // Add name property for backend parsing
      },
      capabilities,
      tokenPricing,
      rateLimits: finalRateLimits,
    };

    setIsSaving(true);

    if (isEditMode && id) {
      // Update existing client - include the ID
      const clientData = new LlmClient({
        ...baseClientData,
        id: id,
      });
      await updateLlmClient(llmName, id, clientData);
    } else {
      // Create new client - ID will be generated by backend
      const clientData = new LlmClient({
        ...baseClientData,
        id: "", // Temporary ID, backend will generate the real one
      });
      await createLlmClient(llmName, {
        ...clientData,
        clientName: selectedClientName,
      });
    }

    setIsSaving(false);
    // Modal will close automatically via useEffect when isCreated/isUpdated becomes true
  };

  const isFormValid =
    selectedClientName &&
    clientRequirements?.every((req) => requirementValues[req]?.trim());

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? `Edit Client: ${selectedClientName}` : "Add Client"}
          </DialogTitle>
        </DialogHeader>

        {isLoadingClients || (isEditMode && isLoadingDetails) ? (
          <CenterLoader />
        ) : (
          <div className="space-y-4">
            {/* Client Name Selection */}
            <div>
              <Label htmlFor="clientName">Client Name</Label>
              <Select
                value={selectedClientName}
                onValueChange={handleClientNameChange}
                disabled={isEditMode}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent>
                  {availableClients?.map((client) => (
                    <SelectItem key={client} value={client}>
                      {client}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Capabilities Section */}
            {selectedClientName && (
              <div className="space-y-4">
                <div className="text-sm font-medium text-gray-700">
                  Capabilities
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="thinking"
                      checked={capabilities.thinking}
                      onCheckedChange={(checked) =>
                        setCapabilities((prev) => ({
                          ...prev,
                          thinking: !!checked,
                        }))
                      }
                    />
                    <Label htmlFor="thinking">Thinking</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="vision"
                      checked={capabilities.vision}
                      onCheckedChange={(checked) =>
                        setCapabilities((prev) => ({
                          ...prev,
                          vision: !!checked,
                        }))
                      }
                    />
                    <Label htmlFor="vision">Vision</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="promptCaching"
                      checked={capabilities.promptCaching}
                      onCheckedChange={(checked) =>
                        setCapabilities((prev) => ({
                          ...prev,
                          promptCaching: !!checked,
                        }))
                      }
                    />
                    <Label htmlFor="promptCaching">Prompt Caching</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="batching"
                      checked={capabilities.batching}
                      onCheckedChange={(checked) =>
                        setCapabilities((prev) => ({
                          ...prev,
                          batching: !!checked,
                        }))
                      }
                    />
                    <Label htmlFor="batching">Batching</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="functionCalling"
                      checked={capabilities.functionCalling}
                      onCheckedChange={(checked) =>
                        setCapabilities((prev) => ({
                          ...prev,
                          functionCalling: !!checked,
                        }))
                      }
                    />
                    <Label htmlFor="functionCalling">Function Calling</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="streaming"
                      checked={capabilities.streaming}
                      onCheckedChange={(checked) =>
                        setCapabilities((prev) => ({
                          ...prev,
                          streaming: !!checked,
                        }))
                      }
                    />
                    <Label htmlFor="streaming">Streaming</Label>
                  </div>
                </div>
              </div>
            )}

            {/* Token Pricing Section */}
            {selectedClientName && (
              <div className="space-y-4">
                <div className="text-sm font-medium text-gray-700">
                  Token Pricing ($ per 1 million tokens)
                </div>

                {/* Basic Pricing */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="inputPrice">
                      Input (per 1 million tokens){" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                        $
                      </span>
                      <Input
                        id="inputPrice"
                        type="number"
                        min="0"
                        step="0.01"
                        value={tokenPricing.input}
                        onChange={(e) =>
                          setTokenPricing((prev) => ({
                            ...prev,
                            input: parseFloat(e.target.value) || 0,
                          }))
                        }
                        placeholder="per 1M tokens"
                        className="pl-8"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="outputPrice">
                      Output (per 1 million tokens){" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                        $
                      </span>
                      <Input
                        id="outputPrice"
                        type="number"
                        min="0"
                        step="0.01"
                        value={tokenPricing.output}
                        onChange={(e) =>
                          setTokenPricing((prev) => ({
                            ...prev,
                            output: parseFloat(e.target.value) || 0,
                          }))
                        }
                        placeholder="per 1M tokens"
                        className="pl-8"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Cache Pricing - only show if prompt caching enabled */}
                {capabilities.promptCaching && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="cacheWrite">
                        Cache Write (per 1 million tokens){" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                          $
                        </span>
                        <Input
                          id="cacheWrite"
                          type="number"
                          min="0"
                          step="0.01"
                          value={tokenPricing.cacheWrite || 0}
                          onChange={(e) =>
                            setTokenPricing((prev) => ({
                              ...prev,
                              cacheWrite: parseFloat(e.target.value) || 0,
                            }))
                          }
                          placeholder="per 1M tokens"
                          className="pl-8"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="cacheRead">
                        Cache Read (per 1 million tokens){" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                          $
                        </span>
                        <Input
                          id="cacheRead"
                          type="number"
                          min="0"
                          step="0.01"
                          value={tokenPricing.cacheRead || 0}
                          onChange={(e) =>
                            setTokenPricing((prev) => ({
                              ...prev,
                              cacheRead: parseFloat(e.target.value) || 0,
                            }))
                          }
                          placeholder="per 1M tokens"
                          className="pl-8"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Batch Pricing - only show if batching enabled */}
                {capabilities.batching && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="batchInput">
                        Batch Input (per 1 million tokens){" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                          $
                        </span>
                        <Input
                          id="batchInput"
                          type="number"
                          min="0"
                          step="0.01"
                          value={tokenPricing.batchInput || 0}
                          onChange={(e) =>
                            setTokenPricing((prev) => ({
                              ...prev,
                              batchInput: parseFloat(e.target.value) || 0,
                            }))
                          }
                          placeholder="per 1M tokens"
                          className="pl-8"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="batchOutput">
                        Batch Output (per 1 million tokens){" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                          $
                        </span>
                        <Input
                          id="batchOutput"
                          type="number"
                          min="0"
                          step="0.01"
                          value={tokenPricing.batchOutput || 0}
                          onChange={(e) =>
                            setTokenPricing((prev) => ({
                              ...prev,
                              batchOutput: parseFloat(e.target.value) || 0,
                            }))
                          }
                          placeholder="per 1M tokens"
                          className="pl-8"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Rate Limits Section */}
            {selectedClientName && (
              <div className="space-y-4">
                <div className="text-sm font-medium text-gray-700">
                  Rate Limits
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={rateLimitEnabled.requestsPerMinute}
                      onCheckedChange={(checked) => {
                        setRateLimitEnabled((prev) => ({
                          ...prev,
                          requestsPerMinute: checked,
                        }));
                        if (!checked) {
                          setRateLimits((prev) => ({
                            ...prev,
                            requestsPerMinute: null,
                          }));
                        }
                      }}
                    />
                    <div className="flex-1">
                      <Label htmlFor="requestsPerMinute">
                        Requests per minute{" "}
                        {rateLimitEnabled.requestsPerMinute && (
                          <span className="text-red-500">*</span>
                        )}
                      </Label>
                      <Input
                        id="requestsPerMinute"
                        type="number"
                        min="0"
                        value={rateLimits.requestsPerMinute || ""}
                        onChange={(e) =>
                          setRateLimits((prev) => ({
                            ...prev,
                            requestsPerMinute: parseInt(e.target.value) || 0,
                          }))
                        }
                        placeholder="Enter limit..."
                        disabled={!rateLimitEnabled.requestsPerMinute}
                        required={rateLimitEnabled.requestsPerMinute}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      checked={rateLimitEnabled.tokensPerMinute}
                      onCheckedChange={(checked) => {
                        setRateLimitEnabled((prev) => ({
                          ...prev,
                          tokensPerMinute: checked,
                        }));
                        if (!checked) {
                          setRateLimits((prev) => ({
                            ...prev,
                            tokensPerMinute: null,
                          }));
                        }
                      }}
                    />
                    <div className="flex-1">
                      <Label htmlFor="tokensPerMinute">
                        Tokens per minute{" "}
                        {rateLimitEnabled.tokensPerMinute && (
                          <span className="text-red-500">*</span>
                        )}
                      </Label>
                      <Input
                        id="tokensPerMinute"
                        type="number"
                        min="0"
                        value={rateLimits.tokensPerMinute || ""}
                        onChange={(e) =>
                          setRateLimits((prev) => ({
                            ...prev,
                            tokensPerMinute: parseInt(e.target.value) || 0,
                          }))
                        }
                        placeholder="Enter limit..."
                        disabled={!rateLimitEnabled.tokensPerMinute}
                        required={rateLimitEnabled.tokensPerMinute}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      checked={rateLimitEnabled.tokenPerDay}
                      onCheckedChange={(checked) => {
                        setRateLimitEnabled((prev) => ({
                          ...prev,
                          tokenPerDay: checked,
                        }));
                        if (!checked) {
                          setRateLimits((prev) => ({
                            ...prev,
                            tokenPerDay: null,
                          }));
                        }
                      }}
                    />
                    <div className="flex-1">
                      <Label htmlFor="tokenPerDay">
                        Tokens per day{" "}
                        {rateLimitEnabled.tokenPerDay && (
                          <span className="text-red-500">*</span>
                        )}
                      </Label>
                      <Input
                        id="tokenPerDay"
                        type="number"
                        min="0"
                        value={rateLimits.tokenPerDay || ""}
                        onChange={(e) =>
                          setRateLimits((prev) => ({
                            ...prev,
                            tokenPerDay: parseInt(e.target.value) || 0,
                          }))
                        }
                        placeholder="Enter limit..."
                        disabled={!rateLimitEnabled.tokenPerDay}
                        required={rateLimitEnabled.tokenPerDay}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* AI Gateway Requirements Section */}
            {selectedClientName && (
              <>
                {isLoadingRequirements ? (
                  <div className="flex justify-center py-4">
                    <div className="text-sm text-gray-500">
                      Loading requirements...
                    </div>
                  </div>
                ) : clientRequirements && clientRequirements.length > 0 ? (
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-gray-700">
                      AI Gateway Requirements
                    </div>
                    {clientRequirements.map((requirement) => {
                      const description =
                        requirement === "model"
                          ? "Model name used for the client to send actual requests"
                          : "Name of the environment variable on the AI Gateway server";

                      const baseDisplayName =
                        requirement.charAt(0).toUpperCase() +
                        requirement.slice(1);
                      const displayName =
                        requirement === "model"
                          ? baseDisplayName
                          : `${baseDisplayName} environment variable`;

                      return (
                        <div key={requirement}>
                          <Label htmlFor={requirement}>
                            {displayName}{" "}
                            <span className="text-red-500">*</span>
                          </Label>
                          <div className="text-xs text-gray-500 mb-1">
                            {description}
                          </div>
                          <Input
                            id={requirement}
                            value={requirementValues[requirement] || ""}
                            onChange={(e) =>
                              handleRequirementChange(
                                requirement,
                                e.target.value,
                              )
                            }
                            placeholder="Enter value..."
                            required
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 text-center py-4">
                    No AI Gateway requirements for this client.
                  </div>
                )}
              </>
            )}

            {/* Note Field */}
            {selectedClientName && (
              <div>
                <Label htmlFor="note">Note</Label>
                <Input
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Enter optional note..."
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSaving || isLoadingRequirements}
          >
            {isSaving
              ? "Saving..."
              : isEditMode
                ? "Update Client"
                : "Add Client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
